export type DotStatus = "Yes" | "No" | null;
export type DotSource = "SPARK" | "Manual" | "Third party" | null;

export type DotAction =
  | "list_controls"
  | "update_control"
  | "summary_score"
  | "get_settings"
  | "update_settings"
  | "history";

export type DotDeviceResult = "Pass" | "Fail";

export interface DotPidDevice {
  device_id?: string;
  device_name: string;
  device_type?: string;
  vendor?: string;
  model?: string;
  status: DotDeviceResult;
}

export interface DotLinkedPid {
  /** Unique recommendation key (_dedup_norm_id) — same as CIS ControlDetails id */
  norm_id?: string;
  pid: string;
  title: string;
  vendor?: string;
  model?: string;
  bench_id?: string;
  framework: string;
  /** null until scan Pass/Fail join is wired on BE */
  overall: DotDeviceResult | null;
  devices: DotPidDevice[];
}

export interface DotControl {
  id: string;
  title: string;
  description: string;
  note: string;
  section: string;
  section_heading: string;
  subsection: string;
  subsection_heading: string;
  /** Recorded answer used for score (override if set, else auto for PID-mapped, else user) */
  status: DotStatus;
  source: DotSource;
  comments: string;
  updated_at: string | null;
  updated_by: string | null;
  /** True when DoT line has linked SPARK PIDs in masterfile */
  pid_mapped: boolean;
  /**
   * True when PID-mapped but no latest-scan recommendation data yet.
   * Excluded from score; compliance shows NA (not Fail).
   */
  na?: boolean;
  /** Scanned-only linked PIDs (latest ScanDevicesDetails) */
  linked_pids: DotLinkedPid[];
  /** Auto calc from scanned PIDs: all Pass → Yes, any Fail → No */
  auto_status: DotStatus;
  override: boolean;
  override_reason: string;
}

export interface DotSettings {
  stale_days: number;
}

export interface DotSummary {
  total: number;
  yes: number;
  no: number;
  unset: number;
  /** PID-mapped with no scanned recommendations — excluded from score */
  na?: number;
  score: number;
  gaps: number;
  stale: number;
  readiness_pct: number;
  pid_mapped: number;
  overridden: number;
}

export interface DotHistoryEntry {
  id: string;
  control_id: string;
  control_title: string;
  field: string;
  old_value: string;
  new_value: string;
  updated_at: string;
  updated_by: string;
}

export interface DotUpdatePayload {
  control_id: string;
  status?: DotStatus;
  source?: DotSource;
  comments?: string;
  /** Set true when user overrides auto for PID-mapped control */
  override?: boolean;
  override_reason?: string;
  /** Clear override and snap back to auto_status */
  reset_to_auto?: boolean;
}

/** Display label for Source column — SPARK (incl. PID), Manual, Third party */
export function getDotSourceLabel(control: DotControl): string {
  if (control.pid_mapped || control.source === "SPARK") return "SPARK";
  if (control.source === "Manual") return "Manual";
  if (control.source === "Third party") return "Third party";
  return "—";
}
